/**
 * script_cart-actions.js
 *
 * カート管理 + お気に入り管理の同期エンジン。
 * theme.liquid で1度読み込まれ、全ページで動作する。
 *
 * 真実の情報源
 *   カート    : Shopify の通常 POST フォーム送信 + /cart.js (同期用)
 *   お気に入り: localStorage (キー: roi:favorites:v2)
 *
 * カート追加の流れ
 *   フォームをそのまま送信 → Shopify が /cart/add を処理 → return_to=/cart でカート画面へ遷移
 *   COMING SOON 商品のみ preventDefault で送信を止める
 *
 * カードの状態判定の優先順 (1枚ごとに適用)
 *   ① data-launch-date が未来        → COMING SOON (disabled)
 *   ② data-variant-available="false" → SOLD OUT (disabled、文字色だけ --secondary-color)
 *   ③ Cart API の items に variantId が存在、または在庫追跡・販売不可で在庫上限に達している
 *                                    → カゴに追加済み (is-added / disabled)
 *   ④ それ以外                       → カゴに追加 (カート確認後に表示)
 */

/* ============================================================
   将来の拡張: ストレージ抽象化（ログイン後のデバイス間同期に備える）
   ============================================================ */

/**
 * [設計メモ] ログイン機能を実装する際は、以下の抽象クラスを具体実装に差し替える。
 *
 *   class LocalFavoritesStore {
 *     list()        → roi:favorites:v2 の全エントリを配列で返す
 *     has(id)       → id がお気に入りに含まれるか判定する
 *     toggle(entry) → 登録/解除を切り替えて保存する
 *     count()       → 登録件数を返す
 *     subscribe(cb) → storage / CustomEvent で変更を通知する
 *   }
 *
 *   class RemoteFavoritesStore {
 *     list()        → ログイン後: Shopify 顧客 API または App Proxy 経由で取得する
 *     has(id)       → 同上
 *     toggle(entry) → 同上
 *     count()       → 同上
 *     subscribe(cb) → Server-Sent Events / ポーリングで変更を通知する
 *   }
 *
 * 切り替え戦略（ログイン検知時）:
 *   1. RemoteFavoritesStore からリモートデータを取得する
 *   2. ローカルデータとマージして重複排除（addedAt が古い方を優先）する
 *   3. マージ結果をリモートに書き戻す
 *   4. ゲスト用ローカルデータをクリアする
 *
 * 現状の実装はすべて LocalFavoritesStore 相当として動作している。
 */

/* ============================================================
   定数
   ============================================================ */

/** お気に入りデータを保存する localStorage のキー名 */
const FAVORITES_KEY = 'roi:favorites:v2';

/**
 * 直近の syncAll() で取得したカートアイテムのキャッシュ。
 * フォーム送信直前の在庫上限チェックに使用する。
 * syncAll() が成功するたびに上書きされる。
 */
let lastKnownCartItems = [];

/* ============================================================
   バリデーション
   ============================================================ */

/**
 * Shopify の商品ID・バリアントIDは数字文字列のみ許可する。
 * それ以外の値は不正入力として弾く。
 */
function isValidId(val) {
  return typeof val === 'string' && /^\d+$/.test(val.trim());
}

/**
 * Shopify の商品ハンドルは小文字英数字とハイフンのみ許可する。
 * XSS や URL インジェクションを防ぐ。
 */
function isValidHandle(val) {
  return typeof val === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(val.trim());
}

/**
 * カードの data-launch-date（UNIX 秒）が未来の場合に true を返す。
 *
 * data-launch-date は UNIX 秒。日時の文字列としては読まない。
 * - 空、または秒として読めない → false（公開中・通常購入可）
 * - その秒より前 → true（COMING SOON）
 * - ちょうどその秒、またはそれ以降 → false（公開済み）
 */
function isComingSoon(card) {
  const raw = card.dataset.launchDate;
  if (!raw || !/^\d+$/.test(raw)) return false;
  return Date.now() < Number(raw) * 1000;
}

/* ============================================================
   お気に入りストレージ（v2: オブジェクト配列形式）
   ============================================================ */

/**
 * localStorage からお気に入りエントリの配列を取得する。
 * 各エントリは { id, handle, url, addedAt } の形式。
 *
 * - JSON.parse の失敗は try/catch で吸収する
 * - id・handle のバリデーションを通過したものだけを返す
 * - url は /products/ 始まりの相対パスのみ許可する（XSS 対策）
 */
function getFavoritesV2() {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const id = String(entry.id ?? '');
      const handle = String(entry.handle ?? '');
      const url = String(entry.url ?? '');
      // id と handle のバリデーション
      if (!isValidId(id) || !isValidHandle(handle)) return false;
      // url は /products/ 始まりの相対パスのみ許可する（javascript: 等を排除）
      if (!/^\/products\/[a-zA-Z0-9][a-zA-Z0-9\-_]*/.test(url)) return false;
      return true;
    });
  } catch {
    // localStorage が使えない環境（プライベートブラウズなど）は空を返す
    return [];
  }
}

/**
 * お気に入りエントリの配列を localStorage に保存する。
 * 書き込みに失敗しても例外を外に出さない。
 */
function saveFavoritesV2(entries) {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(entries));
  } catch { /* 書き込み不可の場合は無視 */ }
}

/**
 * お気に入りエントリ配列から商品ID だけを取り出した Set を返す。
 * applyFavoriteState / renderAllCards など既存のUI関数に渡すために使用する。
 */
function getFavorites() {
  return new Set(getFavoritesV2().map((e) => String(e.id)));
}

/* ============================================================
   お気に入り変更通知
   ============================================================ */

/**
 * 同一タブ内の他のスクリプト（script_favorites-page.js など）に
 * お気に入りが変更されたことを CustomEvent で通知する。
 * 別タブへの通知は localStorage の storage イベントが担う。
 */
function notifyFavoritesChanged() {
  document.dispatchEvent(new CustomEvent('roi:favorites:changed'));
}

/* ============================================================
   Cart API
   ============================================================ */

/**
 * /cart.js を GET してカートオブジェクト全体を返す。
 * 通信失敗時は例外を投げる（呼び出し元で catch する）。
 */
async function fetchCart() {
  const res = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

/* ============================================================
   UI: カートボタンのラベルテキストを安全に更新するヘルパー
   ============================================================ */

/* ボタン文言。ページごとに変えない。 */
const CART_LABEL_ADD = 'カゴに追加';
const CART_LABEL_ADDED = 'カゴに追加済み';
const CART_LABEL_SOLD_OUT = 'SOLD OUT';
const CART_LABEL_COMING_SOON = 'COMING SOON';

/**
 * カートボタンのラベルテキストだけを安全に更新する。
 *
 * data-cart-label-mode="price" があるボタン（商品詳細CTA）は
 * .product-cta__label 要素のテキストだけを書き換え、
 * 隣接する価格表示 DOM (.product-cta__price) には触らない。
 *
 * SOLD OUT だけ is-sold-out。公開前は is-coming-soon で、赤文字にしない。
 * 文言が決まったボタンは hidden を外す。
 */
function setCartButtonLabel(btn, text) {
  btn.classList.toggle('is-sold-out', text === CART_LABEL_SOLD_OUT);
  btn.classList.toggle('is-coming-soon', text === CART_LABEL_COMING_SOON);
  if (btn.dataset.cartLabelMode === 'price') {
    const labelEl = btn.querySelector('.product-cta__label');
    if (labelEl) {
      labelEl.textContent = text;
      btn.hidden = false;
      return;
    }
  }
  btn.textContent = text;
  btn.hidden = false;
}

/* 公開前と売り切れのときだけ、購入手続きへを押せなくする。 */
function updateCheckoutButton(card, blocked) {
  const checkout = card.querySelector('.product-cta__checkout');
  if (checkout) checkout.disabled = blocked;
}

/* ============================================================
   UI: カード単体のカートボタン状態を更新する
   ============================================================ */

/**
 * 1枚のカード (.products-grid__item) のカートボタンを
 * 現在のカート内容に基づいて描画する。
 *
 * 判定順は必ずこの順番で行う（逆にするとバグる）:
 *   ① data-launch-date が未来に設定されている → COMING SOON（公開前）
 *   ② available = false → SOLD OUT
 *   ③ Cart API の items に variant が存在、または在庫上限 → カゴに追加済み
 *   ④ それ以外 → カゴに追加
 */
function applyCartState(card, cartItems) {
  const variantId = card.dataset.variantId;
  const btn = card.querySelector('.products-grid__actions-cart');
  if (!btn) return;

  // ① 公開前: launch_date が設定されていてまだ未来の場合（最優先で判定）
  if (isComingSoon(card)) {
    btn.disabled = true;
    btn.classList.remove('is-added');
    setCartButtonLabel(btn, CART_LABEL_COMING_SOON);
    updateCheckoutButton(card, true);
    return;
  }

  // ② 在庫なし / 購入不可 (Liquid が data-variant-available="false" を出力)
  const available = card.dataset.variantAvailable !== 'false';
  if (!available) {
    btn.disabled = true;
    btn.classList.remove('is-added');
    setCartButtonLabel(btn, CART_LABEL_SOLD_OUT);
    updateCheckoutButton(card, true);
    return;
  }

  // ③ このカードのバリアントのカート内合計数量を確認する
  //    在庫追跡・販売不可 (deny) の商品は在庫数に達した場合も追加済み扱いにする
  const cartQty = isValidId(variantId)
    ? cartItems
        .filter((item) => String(item.variant_id) === variantId)
        .reduce((sum, item) => sum + item.quantity, 0)
    : 0;
  const inventoryQty = parseInt(card.dataset.inventoryQuantity, 10);
  // 在庫追跡あり・販売不可で在庫上限に達している場合は追加済みと同じ扱いにする
  const atInventoryLimit =
    isStrictInventory(card) && !isNaN(inventoryQty) && cartQty >= inventoryQty;
  // バリアントがカートに1点でも入っている場合も追加済みとして扱う
  const inCart = cartQty > 0;

  if (inCart || atInventoryLimit) {
    btn.disabled = true;
    btn.classList.add('is-added');
    setCartButtonLabel(btn, CART_LABEL_ADDED);
    updateCheckoutButton(card, false);
  } else {
    btn.disabled = false;
    btn.classList.remove('is-added');
    setCartButtonLabel(btn, CART_LABEL_ADD);
    updateCheckoutButton(card, false);
  }
}

/* ============================================================
   UI: カード単体のお気に入りボタン状態を更新する
   ============================================================ */

/**
 * 1枚のカードのお気に入りボタンを
 * 現在の favorites Set（商品ID の集合）に基づいて描画する。
 */
function applyFavoriteState(card, favorites) {
  const productId = card.dataset.productId;
  const btn = card.querySelector('.products-grid__actions-favorite');
  if (!btn || !isValidId(productId)) return;

  const active = favorites.has(productId);
  btn.classList.toggle('is-active', active);
  btn.setAttribute('aria-pressed', String(active));
}

/* ============================================================
   UI: ページ上の全カードをまとめて再描画する
   ============================================================ */

/**
 * ページ内に存在する全ての .products-grid__item を走査して
 * カートとお気に入りの状態を両方更新する。
 */
function renderAllCards(cartItems, favorites) {
  document.querySelectorAll('.products-grid__item').forEach((card) => {
    applyCartState(card, cartItems);
    applyFavoriteState(card, favorites);
  });
}

/* ============================================================
   UI: ヘッダーのカートカウントを更新する
   ============================================================ */

/**
 * ヘッダーの .header__cart-link 内の <sup> を Cart API の件数で更新する。
 * header.liquid はカート0件のとき <sup> を出力しないため、
 * 0→1 になった場合は動的に生成し、1→0 になった場合は削除する。
 */
function updateHeaderCartCount(count) {
  const link = document.querySelector('.header__cart-link');
  if (!link) return;

  let sup = link.querySelector('sup');

  if (count > 0) {
    if (!sup) {
      // <sup> が存在しなければ SVG の直前に挿入する (header.liquid の構造に合わせる)
      sup = document.createElement('sup');
      const svg = link.querySelector('svg');
      link.insertBefore(sup, svg || null);
    }
    sup.textContent = count;
  } else if (sup) {
    // カートが空になったら <sup> ごと消す
    sup.remove();
  }
}

/* ============================================================
   UI: ヘッダーのお気に入りカウントを更新する
   ============================================================ */

/**
 * ヘッダーの .header__favorite-count テキストを
 * 現在のお気に入り件数で更新する。
 */
function updateHeaderFavoriteCount(count) {
  const el = document.querySelector('.header__favorite-count');
  if (el) el.textContent = count;
}

/* ============================================================
   同期: カート + お気に入りをまとめて再同期する (メイン処理)
   ============================================================ */

/**
 * Cart API を取得してページ上の全UIを最新状態に揃える。
 * カート・お気に入りのどちらも常にここを呼んで更新する。
 * 失敗したときは、売り切れと公開前以外を「カゴに追加」にして通常のフォーム送信を使えるようにする。
 */
async function syncAll() {
  try {
    const cart = await fetchCart();
    // フォーム送信前の在庫チェックで参照できるようキャッシュを更新する
    lastKnownCartItems = cart.items;
    const favorites = getFavorites();
    renderAllCards(cart.items, favorites);
    updateHeaderCartCount(cart.item_count);
    updateHeaderFavoriteCount(favorites.size);
    if (document.querySelector('[data-paidy-cart-widget]')) {
      document.dispatchEvent(new CustomEvent('roi:cart:changed', { detail: { cart } }));
    }
  } catch {
    document.querySelectorAll('.products-grid__actions-cart').forEach((btn) => {
      if (btn.classList.contains('is-sold-out') || btn.classList.contains('is-coming-soon')) return;
      const card = btn.closest('.products-grid__item');
      if (card && (isComingSoon(card) || card.dataset.variantAvailable === 'false')) return;
      btn.disabled = false;
      btn.classList.remove('is-added');
      setCartButtonLabel(btn, CART_LABEL_ADD);
    });
  }
}

/**
 * Cart API を呼ばずお気に入りだけ再描画する。
 * localStorage の storage イベント（別タブの変更）を受けた場合に使用する。
 */
function syncFavoritesOnly() {
  const favorites = getFavorites();
  document.querySelectorAll('.products-grid__item').forEach((card) => {
    applyFavoriteState(card, favorites);
  });
  updateHeaderFavoriteCount(favorites.size);
}

/* ============================================================
   在庫ガード: フォーム送信前の判定ヘルパー
   ============================================================ */

/**
 * カードが「Shopify 在庫追跡あり・販売継続不可 (deny)」かどうかを返す。
 * deny のときだけ在庫数を上限として扱う。
 * continue（在庫切れでも購入可）の商品は制限しない。
 */
function isStrictInventory(card) {
  return (
    card.dataset.inventoryManagement === 'shopify' &&
    card.dataset.inventoryPolicy === 'deny'
  );
}

/**
 * lastKnownCartItems から指定バリアントの合計カート数量を返す。
 * バリアントがカートにない場合は 0 を返す。
 */
function getCartQuantity(variantId) {
  return lastKnownCartItems
    .filter((item) => String(item.variant_id) === variantId)
    .reduce((sum, item) => sum + item.quantity, 0);
}

/* ============================================================
   イベント: カートフォームの submit
   ============================================================ */

/**
 * フォーム送信時の処理。以下の順に判定してブロックする。
 *
 * ① COMING SOON    : 公開前の商品は送信を止める（既存の安全弁）
 * ② 二重送信ロック : 同じフォームがすでに送信中なら2回目以降を止める
 *                    （素早いダブルクリックで2点入るバグを防ぐ）
 * ③ 在庫上限チェック: 在庫追跡かつ販売不可 (deny) の商品でカート数量が
 *                    在庫数に達している場合はブロックする
 *
 * ①〜③に該当しない場合は通常送信を行い、ボタンを即座に無効化する。
 */
document.addEventListener('submit', (e) => {
  const form = e.target.closest('.products-grid__add-to-cart');
  if (!form) return;

  const card = form.closest('.products-grid__item');
  const btn = form.querySelector('.products-grid__actions-cart');

  // ① 公開前の商品は送信を止める（COMING SOON ボタンが押された場合の安全弁）
  if (card && isComingSoon(card)) {
    e.preventDefault();
    return;
  }

  // ② 二重送信ロック: すでに送信処理が走っているフォームは2回目以降を止める
  if (form.dataset.submitting === 'true') {
    e.preventDefault();
    return;
  }

  // ③ 在庫上限チェック: 在庫追跡・販売不可の商品で上限に達している場合はブロックする
  if (card) {
    const variantId = card.dataset.variantId;
    if (isValidId(variantId) && isStrictInventory(card)) {
      const inventoryQty = parseInt(card.dataset.inventoryQuantity, 10);
      const cartQty = getCartQuantity(variantId);
      if (!isNaN(inventoryQty) && cartQty >= inventoryQty) {
        e.preventDefault();
        // ボタンを「追加済み」状態にして UI に即反映する
        if (btn) {
          btn.disabled = true;
          setCartButtonLabel(btn, CART_LABEL_ADDED);
          btn.classList.add('is-added');
        }
        return;
      }
    }
  }

  // 通常送信: 送信中フラグを立て、ボタンを無効化してから POST を通す
  form.dataset.submitting = 'true';
  if (btn) {
    btn.disabled = true;
  }
});

/* ============================================================
   イベント: お気に入りボタンのクリック
   ============================================================ */

/**
 * .products-grid__actions-favorite ボタンをクリックしたとき
 * v2 形式（id/handle/url/addedAt）で localStorage を更新し、
 * このタブのUIを即時反映する。
 * 別タブへの反映は storage イベントを通じて行われる。
 * script_favorites-page.js などへの通知は CustomEvent で行う。
 */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.products-grid__actions-favorite');
  if (!btn) return;

  const card = btn.closest('.products-grid__item');
  if (!card) return;

  const productId = card.dataset.productId;
  if (!isValidId(productId)) return;

  // handle が取得できない場合は保存できないのでスキップする
  const handle = card.dataset.productHandle;
  if (!isValidHandle(handle)) return;

  const entries = getFavoritesV2();
  const isAlreadyFavorite = entries.some((e) => String(e.id) === productId);

  let newEntries;
  if (isAlreadyFavorite) {
    // お気に入り解除: 対象エントリを除いた配列を作る
    newEntries = entries.filter((e) => String(e.id) !== productId);
  } else {
    // お気に入り追加: v2 形式のエントリを末尾に追加する
    const url = `/products/${handle}`;
    newEntries = [...entries, { id: productId, handle, url, addedAt: Date.now() }];
  }

  saveFavoritesV2(newEntries);

  // 新しい ID セットを組み立ててこのタブの UI を即時更新する
  const newFavoriteIds = new Set(newEntries.map((e) => String(e.id)));
  applyFavoriteState(card, newFavoriteIds);
  updateHeaderFavoriteCount(newFavoriteIds.size);

  // 同一タブ内の script_favorites-page.js などに変更を通知する
  notifyFavoritesChanged();
});

/* ============================================================
   イベント: 別タブで localStorage が変更されたとき
   ============================================================ */

/**
 * 別タブ・別ウィンドウでお気に入りが変更された場合に
 * storage イベントで通知を受け取り、このタブのUIに反映する。
 * （同一タブでの変更は storage イベントが発火しないため上のクリック処理で対応済み）
 */
window.addEventListener('storage', (e) => {
  if (e.key === FAVORITES_KEY) {
    syncFavoritesOnly();
  }
});

/* ============================================================
   イベント: BFCache（ブラウザの戻る/進む キャッシュ）からの復帰
   ============================================================ */

/**
 * ブラウザの「戻る/進む」でBFCacheから復元されたとき
 * カート状態が古くなっている可能性があるため再同期する。
 * (e.persisted が true のときだけBFCache復元)
 */
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    syncAll();
  }
});

/* ============================================================
   イベント: タブがアクティブになったとき
   ============================================================ */

/**
 * タブを切り替えて戻ってきたとき、他人が購入済みの可能性があるため
 * カートを再同期して売り切れ状態を正しく表示する。
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    syncAll();
  }
});

/* ============================================================
   Shopify Theme Editor 対応
   ============================================================ */

/**
 * テーマエディタでセクションが再読み込みされたとき
 * 新しくDOMに追加されたカードに対して同期を掛け直す。
 */
document.addEventListener('shopify:section:load', () => {
  syncAll();
});

/* ============================================================
   初期化: ページ読み込み時に必ず同期する (全処理の起点)
   ============================================================ */

/**
 * DOMの準備が完了しているかどうかで処理を分岐する。
 * - <head> 内の script_tag で読み込まれる場合 → loading 状態なので DOMContentLoaded を待つ
 * - defer 属性つき、または DOM 構築済みで読み込まれた場合 → 即実行
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAll);
} else {
  syncAll();
}
